import axios, { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import { history } from '../..';
import { IAgent } from '../models/agent';
import { ICheckUploadFileResult } from '../models/checkUploadFileResult';
import { IClerk, IClerkFormValues } from '../models/clerk';
import {
  IDepositSalesTicket,
  IDepositSalesTicketFilters,
  IDepositSalesTicketPrint,
} from '../models/depositSalesTicket';
import { IFrame, ISchedule } from '../models/frame';
import { IQrTicket, IQrTicketFilters } from '../models/qrticket';
import { IReservation } from '../models/reservation';
import { ITicketPrice } from '../models/ticketPrice';

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

axios.interceptors.request.use(
  (config) => {
    const token = window.localStorage.getItem('jwt');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(undefined, (error) => {
  if (error.message === 'Network Error' && !error.response) {
    toast.error('Network error!');
  }
  const { status } = error.response;
  if (status === 401) {
    window.localStorage.removeItem('jwt');
    history.push('/admin');
    return Promise.reject(error);
  }
  if (error.response.status === 404) {
    history.push('/notfound');
  }
  if (status === 500) {
    toast.error('システムエラー!');
  }
  throw error;
});

const responseBody = (response: AxiosResponse) => response.data;

// const sleep = (ms: number) => (response: AxiosResponse) =>
//   new Promise<AxiosResponse>((resolve) =>
//     setTimeout(() => resolve(response), ms)
//   );

const requests = {
  get: (url: string) => axios.get(url).then(responseBody),
  post: (url: string, body: {}) => axios.post(url, body).then(responseBody),
  put: (url: string, body: {}) => axios.put(url, body).then(responseBody),
  del: (url: string) => axios.delete(url).then(responseBody),
  patch: (url: string, body: {}) => axios.patch(url, body).then(responseBody),
  postForm: (url: string, file: File) => {
    let formData = new FormData();
    formData.append('File', file);
    return axios
      .post(url, formData, {
        headers: { 'Content-type': 'multipart/form-data' },
      })
      .then(responseBody);
  },
};

const DepositSalesTickets = {
  attributes: () => requests.get(`/depositsalestickets/attributes`),
  listPrint: (): Promise<IDepositSalesTicketPrint[]> =>
    requests.get('/depositsalestickets/listPrint'),
  list: (
    depositSalesTicketFilters: IDepositSalesTicketFilters
  ): Promise<IDepositSalesTicket[]> =>
    requests.post(`/depositsalestickets/search`, depositSalesTicketFilters),
  details: (salesId: string) => requests.get(`/depositsalestickets/${salesId}`),
  create: (depositSalesTicket: IDepositSalesTicket) =>
    requests.post(`/depositsalestickets`, depositSalesTicket),
  update: (depositSalesTicket: IDepositSalesTicket) =>
    requests.put(
      `/depositsalestickets/${depositSalesTicket.salesId}`,
      depositSalesTicket
    ),
  changeLimit: (depositSalesTicket: IDepositSalesTicket) =>
    requests.put(
      `/depositsalestickets/${depositSalesTicket.salesId}/changeLimit`,
      depositSalesTicket
    ),
  delete: (salesId: string) => requests.del(`/depositsalestickets/${salesId}`),
  issue: (salesId: string) =>
    requests.post(`/depositsalestickets/${salesId}`, {}),
  checkUploadFile: (file: File): Promise<ICheckUploadFileResult[]> =>
    requests.postForm('/depositsalestickets/checkUploadFile', file),
  completeUploadFile: (
    checkUploadFileSuccessfulResults: ICheckUploadFileResult[]
  ) =>
    requests.post(
      `/depositsalestickets/completeUploadFile`,
      checkUploadFileSuccessfulResults
    ),
};

const QrTickets = {
  attributes: () => requests.get(`/qrtickets/attributes`),
  list: (qrTicketFilters: IQrTicketFilters): Promise<IQrTicket[]> =>
    requests.post(`/qrtickets/search`, qrTicketFilters),
  download: (salesId: string): Promise<IQrTicket[]> =>
    requests.post(`/qrtickets/${salesId}/download`, []),
  partiallydownload: (
    salesId: string,
    qrticketIds: number[]
  ): Promise<IQrTicket[]> =>
    requests.post(`/qrtickets/${salesId}/partiallydownload`, qrticketIds),
  print: (salesId: string) => requests.post(`/qrtickets/${salesId}/print`, []),
  partiallyprint: (salesId: string, qrticketIds: number[]) =>
    requests.post(`/qrtickets/${salesId}/partiallyprint`, qrticketIds),
  issue: (salesId: string, qrticketIds: number[]) =>
    requests.post(`/qrtickets/${salesId}`, qrticketIds),
  enter: (qrticketId: number) =>
    requests.patch(`/qrtickets/${qrticketId}/enter`, []),
  exit: (qrticketId: number) =>
    requests.patch(`/qrtickets/${qrticketId}/exit`, []),
  delete: (qrticketIds: number[]) =>
    requests.post('/qrtickets/delete', qrticketIds),
};

const Agents = {
  list: (): Promise<IAgent[]> => requests.get(`/agents`),
};

const Auth = {
  current: (): Promise<IClerk> => requests.get('/auth'),
  signin: (clerk: IClerkFormValues): Promise<IClerk> =>
    requests.post('/auth/signin', clerk),
};

const TicketPrices = {
  list: (
    agentId: string,
    startDate: string,
    mdTdTicketType: string
  ): Promise<ITicketPrice[]> =>
    requests.get(
      `/ticketprice?agentId=${agentId}&startDate=${startDate}&mdTdTicketType=${mdTdTicketType}`
    ),
};

const Reservations = {
  listForAdmin: (reservationDay: string): Promise<IFrame> =>
    requests.get(`/reservations/list/admin?day=${reservationDay}`),
  createFrame: (schedule: ISchedule) =>
    requests.post(`/reservations/admin`, schedule),
  list: (reservationDay: string): Promise<IFrame> =>
    requests.get(`/reservations/list?day=${reservationDay}`),
  checkQrCode: (qr: string): Promise<IReservation> =>
    requests.get(`/reservations?qr=${qr}`),
  create: (reservation: IReservation): Promise<IReservation> =>
    requests.post(`/reservations`, reservation),
  delete: (qrticketId: number): Promise<IReservation> =>
    requests.del(`/reservations/${qrticketId}`),
  sysdate: (): Promise<Date> =>
    requests.get(`/reservations/sysdate`),
};

const Information = {
  list: () => requests.get('/information'),
};

export default {
  DepositSalesTickets,
  QrTickets,
  Agents,
  Auth,
  TicketPrices,
  Reservations,
  Information,
};
